import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core'
import { ActivatedRoute, NavigationExtras, Router } from '@angular/router'
import { ModalController } from '@ionic/angular'
import { combineLatest, Observable, Subject } from 'rxjs'
import { filter, map, takeUntil } from 'rxjs/operators'
import { Stock } from '../../models/stock'
import { StockRepository } from '../../repositories/stock.repository'
import { ImportFileType } from '../../services/file-handler.service'
import { FileService } from '../../services/file.service'
import { StockService } from '../../services/stock.service'
import { Translation } from '../../services/translation.service'
import { SidebarPosition } from '../../shared/components/efp-sidebar-container/efp-sidebar-container.component'
import { PreviousRouteService } from '../../services/previous-route.service'
import { ModalComponent, ModalDismissAction, ModalStyle } from '@doka-shared/common'
import { CommonSettingsRepository } from '../../repositories/common-settings.repository'
import { Doka360ProjectSelectorService } from '../../services/doka360-project-selector.service'
import { DeviceModeRepository } from '../../repositories/device-mode.repository'

export const NO_STOCK_SELECTED = -1

@Component({
  selector: 'efp-stock',
  templateUrl: './stock.page.html',
})
export class StockPage implements OnInit, OnDestroy {
  public navLink = '/homepage'
  public backNavOptions: NavigationExtras | undefined

  public showButtons = true

  public filteredStocks$ = combineLatest([
    this.stockRepository.stocks$,
    this.doka360ProjectSelectorService.currentDoka360ProjectUid$,
  ]).pipe(
    map(([stocks, currentDoka360ProjectUid]) => {
      return stocks.filter((stock) => {
        if (!!currentDoka360ProjectUid) {
          return stock.doka360ProjectUid === currentDoka360ProjectUid
        } else {
          return stock.doka360ProjectUid === undefined
        }
      })
    })
  )
  public title$: Observable<string> =
    this.doka360ProjectSelectorService.currentDoka360ProjectUid$.pipe(
      map((uid) =>
        uid
          ? this.translate.translate('STOCK.PROJECT_NAME')
          : this.translate.translate('STOCK.NAME')
      ),
      filter((title) => !!title) // filter out empty titles
    )

  protected readonly SidebarPosition = SidebarPosition

  private readonly paramsLoaded$ = new Subject<void>()

  constructor(
    public readonly translate: Translation,
    public readonly stockRepository: StockRepository,
    private readonly stockService: StockService,
    private readonly fileService: FileService,
    private readonly cd: ChangeDetectorRef,
    private readonly activeRoute: ActivatedRoute,
    private readonly previousRouteService: PreviousRouteService,
    private readonly router: Router,
    private readonly modalCtrl: ModalController,
    private readonly commonSettingsRepository: CommonSettingsRepository, // used to initilize the common settings repository and get the resourceUrl
    private readonly doka360ProjectSelectorService: Doka360ProjectSelectorService,
    private readonly deviceModeRepository: DeviceModeRepository
  ) {
    this.activeRoute.queryParams.pipe(takeUntil(this.paramsLoaded$)).subscribe((params) => {
      this.navLink = params?.backUrl ?? '/homepage'
      this.backNavOptions = params?.backNavOptions ? JSON.parse(params?.backNavOptions) : undefined
    })
  }

  ngOnInit(): void {
    void this.stockRepository.fetchAllByScopeSelector()
  }

  ngOnDestroy(): void {
    this.paramsLoaded$.next()
  }

  public async showStock(stockId: number): Promise<void> {
    await this.router.navigate(['/stockdetail/' + stockId])
  }

  public async importStock(fileInput: HTMLInputElement): Promise<void> {
    if (!this.deviceModeRepository.isMobileSignal()) {
      // file reading handled with onchange in readFile
      fileInput.click()
    } else {
      // This methods could also be used for web,
      // but you cannot filter for file extension, only for mime types which is not so good for ma7 files
      const file = await this.fileService.pickFile([ImportFileType.csv, ImportFileType.ma7])
      if (file?.data) {
        const decodedFromBase64 = atob(file.data)
        await this.importStockFromFile(decodedFromBase64, file.name)
      }
    }
  }

  async createStock(): Promise<void> {
    const backNavOptions: NavigationExtras | undefined = undefined

    const stock = await this.stockService.createStock()
    if (stock) {
      await this.router.navigate([`/stockdetail/${stock.id}`], {
        queryParams: {
          backUrl: this.router.url.split('?')[0],
          backNavOptions: JSON.stringify(backNavOptions),
        },
      })
    }
  }

  async readFile(fileInput: HTMLInputElement): Promise<void> {
    const file = fileInput.files
    if (file) {
      await this.readFileList(file)
    }

    // if user uploads the same file twice, the onchange event is not triggered. reset Input:
    fileInput.value = ''
  }

  async readFileList(fileList: FileList): Promise<void> {
    if (fileList?.length) {
      const reader = new FileReader()

      reader.readAsText(fileList[0], 'UTF-8')

      const fileTextResult = await new Promise<string>((resolve, reject) => {
        reader.onload = (evt) => {
          resolve(<string>evt.target?.result)
        }
        reader.onerror = (error) => {
          reject(error)
        }
      })

      if (!!fileTextResult && !!fileList[0].name) {
        await this.importStockFromFile(fileTextResult, fileList[0].name)
      }
    }
  }

  private async importStockFromFile(
    text: string | undefined,
    fileName: string | undefined
  ): Promise<void> {
    if (!!text && !!fileName) {
      const stock = await this.stockService.importStockListFromFile(text, fileName)
      if (stock) {
        await this.router.navigate(['/stockdetail/' + stock.id])
      }
    }
  }

  async presentRenameStockAlert(stock: Stock): Promise<void> {
    const modal = await this.stockService.updateOrCreateStockModal(stock.name, stock)
    await modal.present()

    const { data, role } = await modal.onDidDismiss()
    if (role === ModalDismissAction.CONFIRM && data !== stock.name) {
      await this.stockRepository.updateName(stock, data)
    }
  }

  public async deleteStock(stock: Stock): Promise<void> {
    const relatedNames = await this.stockRepository.findAllRelatedPlanAndProjectNamesById(stock.id)
    const alertMessage =
      relatedNames?.length > 0
        ? this.translate.translate('STOCK.STOCK_DELETE_IN_USE_WARNING') +
          '</br><ul><li>' +
          relatedNames.toString() +
          '</li></ul>'
        : this.translate.translate('STOCK.DELETE_MESSAGE')

    const modal = await this.modalCtrl.create({
      component: ModalComponent,
      backdropDismiss: false,
      cssClass: 'small-modal',
      componentProps: {
        title: this.translate.translate('GENERAL.WARNING'),
        innerHTML: alertMessage,
        cancelButtonLabel: this.translate.translate('GENERAL.CANCEL'),
        criticalActionButtonLabel: this.translate.translate('GENERAL.DELETE'),
        modalStyle: ModalStyle.Alert,
        icon: 'error',
      },
    })
    await modal.present()
    const { role } = await modal.onDidDismiss()
    if (role === ModalDismissAction.CRITICAL_ACTION) {
      await this.stockRepository.delete(stock)
      this.cd.detectChanges()
    }
  }

  async shareStock(stock: Stock): Promise<void> {
    // create csv file
    let csv =
      this.translate.translate('GENERAL.ARTICLE_NUMBER') +
      ';' +
      this.translate.translate('GENERAL.QUANTITY') +
      ';' +
      this.translate.translate('GENERAL.ARTICLE_NAME') +
      '\n'

    const stockWithArticles = await this.stockRepository.findOneById(stock.id)
    if (!stockWithArticles) {
      throw new Error('StockPage.shareStock - Stock not found')
    }
    stockWithArticles.articles?.forEach((article) => {
      csv += article.articleId + ';' + article.amount + ';' + article.name + '\n'
    })

    await this.fileService.shareOrDownloadTextFile(
      csv,
      `exportStock/${stock.name}.csv`,
      `${stock.name}.csv`
    )
  }

  onEnter(): void {
    const focusedElement = document.activeElement as HTMLElement
    if (focusedElement.tagName === 'ION-ITEM') {
      const clickableIcon = focusedElement.querySelector('ion-icon') as HTMLElement
      if (clickableIcon) {
        clickableIcon.click()
      }
    }
  }
}
